import axios from 'axios';
import { Task, TaskFilter, TaskStats, CreateTaskData, UpdateTaskData } from '../types/Task';

// API URL configuration
// In Docker: use service name 'backend'
// In development: use localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8092/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000, // 90 seconds timeout for AI operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const getToken = () => localStorage.getItem('auth_token');
const setToken = (token: string) => localStorage.setItem('auth_token', token);
const removeToken = () => {
  localStorage.removeItem('auth_token');
  // Also clear any other auth-related data that might be stored
  localStorage.removeItem('user');
  localStorage.removeItem('organization');
};

// Request interceptor for authentication and logging
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error.response?.data || error.message);
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      console.log('🔒 Authentication failed - clearing token and redirecting to login');
      removeToken();
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/app/login') {
        window.location.href = '/app/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Types for authentication
export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_email_verified: boolean;
  organization_id?: number;
  organization?: Organization;
  role: string;
  is_active: boolean;
  can_use_ai_tools: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Organization {
  id: number;
  name: string;
  description: string;
  owner_id: number;
  owner: User;
  users?: User[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrganizationInvite {
  id: number;
  email: string;
  organization_id: number;
  organization: Organization;
  invited_by_id: number;
  invited_by: User;
  role: string;
  expires_at: string;
  accepted_at?: string;
  created_at: string;
  updated_at: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface CreateOrganizationRequest {
  name: string;
  description?: string;
}

export interface InviteUserRequest {
  email: string;
  role: 'member' | 'admin';
}

export interface TasksResponse {
  tasks: Task[];
  count: number;
}

export interface KanbanColumn {
  id: string;
  title: string;
  status: string;
  color: string;
  order: number;
  is_protected: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ColumnsResponse {
  columns: KanbanColumn[];
  count: number;
}

export interface CreateColumnData {
  title: string;
  status: string;
  color?: string;
}

export interface UpdateColumnData {
  title?: string;
  status?: string;
  color?: string;
  order?: number;
}

export const taskApi = {
  // Get all tasks with optional filtering
  getTasks: async (filter?: TaskFilter): Promise<TasksResponse> => {
    const params = new URLSearchParams();
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
    }
    
    const response = await api.get(`/tasks?${params.toString()}`);
    return response.data;
  },

  // Get single task by ID
  getTask: async (id: string): Promise<Task> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },

  // Create new task
  createTask: async (taskData: CreateTaskData): Promise<Task> => {
    const response = await api.post('/tasks', taskData);
    return response.data;
  },

  // Update existing task
  updateTask: async (id: string, taskData: UpdateTaskData): Promise<Task> => {
    const response = await api.put(`/tasks/${id}`, taskData);
    return response.data;
  },

  // Delete task
  deleteTask: async (id: string): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },

  // Get task statistics
  getStats: async (): Promise<TaskStats> => {
    const response = await api.get('/stats');
    return response.data;
  },

  // Get available priorities
  getPriorities: async (): Promise<string[]> => {
    const response = await api.get('/priorities');
    return response.data.priorities;
  },

  // Get available statuses
  getStatuses: async (): Promise<string[]> => {
    const response = await api.get('/statuses');
    return response.data.statuses;
  },

  // Upload images to a task
  uploadImages: async (taskId: string, files: File[]): Promise<{ message: string; images: any[]; count: number; errors?: string[]; error_count?: number }> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });

    console.log('🚀 DEBUG: About to send FormData:', formData);
    console.log('🚀 DEBUG: Files in FormData:', Array.from(formData.entries()));

    // Use fetch instead of axios to avoid interceptor issues
    const token = getToken();
    const headers: any = {
      'Authorization': token ? `Bearer ${token}` : '',
      // Don't set Content-Type - let browser set it automatically with boundary
    };
    
    console.log('🚀 DEBUG: Request headers (before fetch):', headers);
    console.log('🚀 DEBUG: API URL:', `${API_BASE_URL}/tasks/${taskId}/images`);
    console.log('🚀 DEBUG: FormData type:', formData.constructor.name);
    console.log('🚀 DEBUG: FormData entries count:', Array.from(formData.entries()).length);

    try {
      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/images`, {
        method: 'POST',
        headers,
        body: formData,
      });

      console.log('🚀 DEBUG: Response status:', response.status);
      console.log('🚀 DEBUG: Response headers:', Object.fromEntries(response.headers.entries()));
      console.log('🚀 DEBUG: Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        console.error('❌ DEBUG: Error response data:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ DEBUG: Success response data:', result);
      return result;
    } catch (error) {
      console.error('❌ DEBUG: Fetch error:', error);
      throw error;
    }
  },

  // Get images for a task
  getTaskImages: async (taskId: string): Promise<{ images: any[]; count: number }> => {
    const response = await api.get(`/tasks/${taskId}/images`);
    return response.data;
  },

  // Get image URL for display
  getImageUrl: (imageId: string): string => {
    const token = getToken();
    if (token) {
      return `${API_BASE_URL}/tasks/images/${imageId}?token=${encodeURIComponent(token)}`;
    }
    return `${API_BASE_URL}/tasks/images/${imageId}`;
  },

  // Delete a task image
  deleteImage: async (imageId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/tasks/images/${imageId}`);
    return response.data;
  },
};

export const columnApi = {
  // Get all columns
  getColumns: async (): Promise<ColumnsResponse> => {
    const response = await api.get('/columns');
    return response.data;
  },

  // Get single column by ID
  getColumn: async (id: string): Promise<KanbanColumn> => {
    const response = await api.get(`/columns/${id}`);
    return response.data;
  },

  // Create new column
  createColumn: async (columnData: CreateColumnData): Promise<KanbanColumn> => {
    const response = await api.post('/columns', columnData);
    return response.data;
  },

  // Update existing column
  updateColumn: async (id: string, columnData: UpdateColumnData): Promise<KanbanColumn> => {
    const response = await api.put(`/columns/${id}`, columnData);
    return response.data;
  },

  // Delete column
  deleteColumn: async (id: string): Promise<void> => {
    await api.delete(`/columns/${id}`);
  },

  // Update column order
  updateColumnOrder: async (columnOrders: { id: string; order: number }[]): Promise<void> => {
    await api.put('/columns/order', { columnOrders });
  },

  // Reset to default columns
  resetToDefault: async (): Promise<void> => {
    await api.post('/columns/reset');
  },
};

export const healthApi = {
  // Health check
  check: async (): Promise<{ status: string; message: string; version: string }> => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Authentication API
export const authApi = {
  // Register new user
  register: async (data: RegisterRequest): Promise<{ message: string; user_id: number; token?: string; user?: User }> => {
    const response = await api.post('/auth/register', data);
    
    // If token is returned, set it for auto-login
    if (response.data.token) {
      setToken(response.data.token);
    }
    
    return response.data;
  },

  // Login user
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data);
    const { token, user } = response.data;
    setToken(token);
    return { token, user };
  },

  // Logout user
  logout: () => {
    removeToken();
  },

  // Verify email
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await api.get(`/auth/verify-email?token=${token}`);
    return response.data;
  },

  // Resend verification email
  resendVerification: async (email: string): Promise<{ message: string }> => {
    const formData = new FormData();
    formData.append('email', email);
    const response = await api.post('/auth/resend-verification', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const formData = new FormData();
    formData.append('email', email);
    const response = await api.post('/auth/forgot-password', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Reset password
  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },

  // Get current user profile
  getProfile: async (): Promise<User> => {
    const response = await api.get('/profile');
    return response.data;
  },

  // Update current user profile
  updateProfile: async (data: { first_name: string; last_name: string; email: string }): Promise<{ message: string; user: User; token: string }> => {
    const response = await api.put('/profile', data);
    const { token } = response.data;
    setToken(token); // Update the token with new email
    return response.data;
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!getToken();
  },
};

// Organization API
export const organizationApi = {
  // Create organization
  create: async (data: CreateOrganizationRequest): Promise<{ organization: Organization; token: string }> => {
    const response = await api.post('/organizations', data);
    const { organization, token } = response.data;
    setToken(token); // Update the token with the new organization_id
    return { organization, token };
  },

  // Get current organization
  getCurrent: async (): Promise<Organization> => {
    const response = await api.get('/organizations/current');
    return response.data;
  },

  // Update organization
  update: async (data: CreateOrganizationRequest): Promise<Organization> => {
    const response = await api.put('/organizations/current', data);
    return response.data;
  },

  // Invite user to organization
  inviteUser: async (data: InviteUserRequest): Promise<{ message: string; invite_id: number }> => {
    const response = await api.post('/organizations/invite', data);
    return response.data;
  },

  // Get pending invites
  getInvites: async (): Promise<OrganizationInvite[]> => {
    const response = await api.get('/organizations/invites');
    return response.data;
  },

  // Remove pending invitation
  removeInvite: async (inviteId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/organizations/invites/${inviteId}`);
    return response.data;
  },

  // Remove user from organization
  removeUser: async (userId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/organizations/users/${userId}`);
    return response.data;
  },

  // Accept organization invite
  acceptInvite: async (token: string): Promise<{ message: string; organization: Organization }> => {
    const response = await api.get(`/invites/accept?token=${token}`);
    return response.data;
  },

  // Get invite information without accepting
  getInviteInfo: async (token: string): Promise<{ invite: any; user_exists: boolean }> => {
    const response = await api.get(`/invites/info?token=${token}`);
    return response.data;
  },

  // Register with invite (for new users)
  registerWithInvite: async (token: string, userData: { first_name: string; last_name: string; password: string }): Promise<{ message: string; user: User; organization: Organization; token: string }> => {
    const response = await api.post(`/invites/register?token=${token}`, userData);
    const { token: newToken } = response.data;
    setToken(newToken); // Set the new token
    return response.data;
  },

  // Leave current organization
  leave: async (): Promise<{ message: string; token: string }> => {
    const response = await api.post('/organizations/leave');
    const { message, token } = response.data;
    setToken(token); // Update the token without organization_id
    return { message, token };
  },

  // Clear all tasks (admin/owner only)
  clearAllTasks: async (): Promise<{ message: string }> => {
    const response = await api.delete('/organizations/tasks');
    return response.data;
  },

  // Clear all non-protected columns (admin/owner only)
  clearAllColumns: async (): Promise<{ message: string }> => {
    const response = await api.delete('/organizations/columns');
    return response.data;
  },

  // Reset all organization data (owner only)
  resetAllData: async (): Promise<{ message: string }> => {
    const response = await api.post('/organizations/reset');
    return response.data;
  },

  // Import CSV file with Polish headers (admin/owner only)
  importCSV: async (file: File, createNewColumns: boolean): Promise<{ message: string; summary: any; details: any[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('createNewColumns', createNewColumns.toString());
    
    const response = await api.post('/organizations/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// AI API
export interface AIAnalysisRequest {
  query: string;
}

export interface AIAnalysisResponse {
  success: boolean;
  response: string;
  analysis?: any;
  tasks_count: number;
  ai_powered: boolean;
}

export interface AIStatusResponse {
  ai_available: boolean;
  message: string;
}

export const aiApi = {
  // Analyze tasks with AI
  analyzeTasks: async (query: string): Promise<AIAnalysisResponse> => {
    const response = await api.post('/ai/analyze', { query });
    return response.data;
  },

  // Get AI service status
  getStatus: async (): Promise<AIStatusResponse> => {
    const response = await api.get('/ai/status');
    return response.data;
  },
};

export default api; 